import React, { useEffect, useRef, useState } from 'react';
import { TokenAddressItem } from 'structure/home/components/TokenAddressItem';
import { Icon } from '@dapp/multi-lib/sdaoui/Icon';
import { ButtonIcon } from '@dapp/multi-lib/sdaoui/ButtonIcon';
import { cx } from 'class-variance-authority';
import { Normal3 } from 'components/Typography';
import { getNetworkIconUrlByChaiId } from 'utils/url';
import { TokenImage } from 'components/TokenImage';

type Option = {
  chainId: string;
  token: {
    title: string;
    address: string;
    url: string;
  };
};

type Props = {
  options: Option[];
  value?: string;
  onChange: (value: string) => void;
};

export const TokenAddressesDropDown = ({ options, value, onChange }: Props) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.chainId === value);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleOnChange = (option: Option) => {
    onChange(option.chainId);
    setOpen(false);
  };

  return (
    <div ref={ref} className='relative w-full'>
      <div className='flex flex-row items-center gap-2'>
        <TokenAddressItem
          chainId={selected?.chainId ?? ''}
          token={selected?.token ?? { title: '', address: '', url: '' }}
        />
        <ButtonIcon onClick={() => setOpen((prev) => !prev)}>
          <Icon
            icon='arrow_down'
            height={24}
            width={24}
            color='#B5B5B5'
            className={cx('transition-all duration-500 ease-in-out', {
              '-rotate-180': open,
            })}
          />
        </ButtonIcon>
      </div>

      {open && (
        <ul className='absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-200 bg-black py-1 text-sm shadow-lg'>
          {options.map((option) => (
            <div
              key={option.chainId}
              className='flex flex-row items-center gap-2 px-2 py-2'
              onClick={() => handleOnChange(option)}
            >
              <TokenImage
                symbol={'ETH'}
                src={getNetworkIconUrlByChaiId(Number(option.chainId))}
                alt={option.token.title}
                width={24}
                height={24}
              />
              <Normal3 className='text-xs font-bold text-white-400 md:text-sm'>
                {option.token.title}
              </Normal3>
            </div>
          ))}
        </ul>
      )}
    </div>
  );
};
